import { useMemo } from 'react';
import {
  useTable,
  ColumnDef,
  ExpandedState,
  ColumnFiltersState,
  ColumnVisibilityState,
  ColumnOrderState,
  OnChangeFn,
  Table,
} from '@tanstack/react-table';
import { SortConfig } from '../types';
import { multiValueFilter, cleosTableFeatures, type CleosTableFeaturesType } from '../tableFeatures';

interface UseTableInstanceOptions<TData extends object> {
  rows: TData[];
  columnDefs: ColumnDef<CleosTableFeaturesType, TData>[];
  columnVisibility: ColumnVisibilityState;
  columnOrder: ColumnOrderState;
  expanded: ExpandedState;
  setExpanded: OnChangeFn<ExpandedState>;
  columnFilters: ColumnFiltersState;
  sortConfig?: SortConfig;
  manualSorting?: boolean;
  // Feeds getRowId below — TableRow.tsx writes row.id verbatim as the <tr>'s DOM id, so a
  // caller's rowIdKey (e.g. SalesAnnexes' "msgSubjectId") must actually reach it.
  rowIdKey: string;
}

export const useTableInstance = <TData extends object>({
  rows,
  columnDefs,
  columnVisibility,
  columnOrder,
  expanded,
  setExpanded,
  columnFilters,
  sortConfig,
  manualSorting = true,
  rowIdKey,
}: UseTableInstanceOptions<TData>): Table<CleosTableFeaturesType, TData> => {
  const sorting = useMemo(
    () =>
      Object.entries(sortConfig?.sortMap ?? {})
        .filter(([, dir]) => dir)
        .map(([id, dir]) => ({ id, desc: dir === 'DESC' })),
    [sortConfig?.sortMap]
  );

  const table = useTable<CleosTableFeaturesType, TData>({
    features: cleosTableFeatures,
    data: rows,
    columns: columnDefs,
    state: {
      columnVisibility,
      columnOrder,
      expanded,
      columnFilters,
      sorting,
    },
    manualSorting,
    enableMultiSort: false,
    enableSortingRemoval: false,
    onExpandedChange: setExpanded,
    onSortingChange: sortConfig
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(sorting) : updater;
          if (next.length === 0) {
            sortConfig.clearSorting?.();
          } else {
            const { id, desc } = next[0];
            sortConfig.handleSorting(id, desc ? 'DESC' : 'ASC');
          }
        }
      : undefined,
    defaultColumn: { filterFn: multiValueFilter },
    getRowId: (row, index) => {
      const r = row as Record<string, unknown>;
      return r[rowIdKey] != null ? String(r[rowIdKey]) : String(index);
    },
  });

  return table;
};
